import { useMemo, useState } from 'react'
import {
  BarElement,
  CategoryScale,
  Chart as ChartJS,
  Legend,
  LinearScale,
  Tooltip,
  type ChartData,
  type ChartOptions,
  type Plugin,
} from 'chart.js'
import { Bar } from 'react-chartjs-2'
import type { IndiceCestaSection, IndiceCestaWeeklyBrandRow } from '../../types/payload'
import { ChartExportButton } from '../charts/ChartExportButton'
import styles from './Section.module.css'

interface Props {
  section: IndiceCestaSection
}

type MetricMode = 'share' | 'brands' | 'skus'
type SegmentName = 'Fralda Infantil' | 'Lenço Umedecido' | 'Sabonete Líquido'
type DensityRecord = {
  formato: string
  faixa_preco: string
  value: number
  total?: number
}

const CHANNEL_ORDER = ['farma', 'digital_nativo', 'atacados_e_atacarejos', 'varejo'] as const

const CHANNEL_LABELS: Record<(typeof CHANNEL_ORDER)[number], string> = {
  farma: 'Farma',
  digital_nativo: 'Digital Nativo',
  atacados_e_atacarejos: 'Atacados e Atacarejos',
  varejo: 'Varejo',
}

const WEEK_LABELS = Array.from({ length: 12 }, (_, index) => `S${String(index + 1).padStart(2, '0')}`)
const SEGMENT_OPTIONS: SegmentName[] = ['Fralda Infantil', 'Lenço Umedecido', 'Sabonete Líquido']
const SEGMENT_UNIT_NOTE: Record<SegmentName, string> = {
  'Fralda Infantil': 'Análise calculada em preço por tira.',
  'Lenço Umedecido': 'Análise calculada em preço por unidade.',
  'Sabonete Líquido': 'Análise calculada em preço por ml.',
}

function extendWeeklySeries(values: readonly number[], targetLength = 12) {
  if (values.length >= targetLength) {
    return values.slice(0, targetLength)
  }

  const result = [...values]
  const lastDelta = values.length >= 2 ? values[values.length - 1] - values[values.length - 2] : 0

  while (result.length < targetLength) {
    const nextValue = Math.max(0, result[result.length - 1] + lastDelta)
    result.push(nextValue)
  }

  return result
}

const FRALDA_INFANTIL_BASELINE = [
  { brand: 'EVER BABY', values: [166, 167, 175, 175, 171, 165, 168, 170, 171] },
  { brand: 'PAMPERS', values: [137, 140, 138, 135, 138, 131, 134, 137, 138] },
  { brand: 'QUALITA BABY', values: [131, 138, 140, 136, 138, 131, 127, 114, 113] },
  { brand: 'HUGGIES', values: [126, 126, 125, 125, 127, 122, 123, 127, 124] },
  { brand: 'DAAZ BABY', values: [116, 116, 117, 116, 115, 110, 112, 113, 113] },
  { brand: 'GALINHA PINTADINHA', values: [109, 109, 106, 106, 107, 100, 103, 105, 103] },
  { brand: 'CAPRICHO BABY', values: [99, 91, 78, 77, 80, 138, 156, 75, 78] },
  { brand: 'MAMYPOKO', values: [83, 84, 85, 85, 82, 82, 85, 100, 100] },
  { brand: 'PERSONAL', values: [86, 86, 87, 87, 88, 81, 84, 92, 94] },
  { brand: 'MILI', values: [88, 87, 88, 86, 87, 83, 84, 87, 85] },
  { brand: 'CARREFOUR', values: [87, 85, 85, 85, 85, 92, 57, 98, 98] },
  { brand: 'FLOCK BABY', values: [85, 85, 88, 87, 85, 79, 79, 84, 83] },
  { brand: 'NEEDS BABY', values: [83, 83, 84, 83, 83, 79, 80, 81, 81] },
  { brand: 'CUIDMAIS BABY', values: [82, 82, 83, 82, 81, 78, 79, 80, 79] },
  { brand: 'LOONEY TUNES', values: [69, 69, 70, 83, 82, 79, 79, 78, 80] },
  { brand: 'KIND BABY', values: [54, 54, 52, 52, 49, 50, 52, 54, 60] },
] as const

const SEGMENT_WEEKLY_MOCK: Record<SegmentName, IndiceCestaWeeklyBrandRow[]> = {
  'Fralda Infantil': FRALDA_INFANTIL_BASELINE.map(row => ({
    brand: row.brand,
    values: extendWeeklySeries(row.values),
  })),
  'Lenço Umedecido': [
    { brand: 'PAMPERS', values: [135, 138, 140, 132, 135, 131, 134, 136, 132, 130, 133, 135] },
    { brand: 'HUGGIES', values: [124, 126, 125, 122, 127, 122, 123, 125, 124, 121, 123, 122] },
    { brand: 'JOHNSONS', values: [118, 119, 121, 116, 115, 112, 114, 113, 111, 110, 112, 115] },
    { brand: 'QUALITÁ (MP)', values: [98, 96, 95, 97, 94, 92, 91, 89, 92, 90, 88, 87] },
    { brand: 'CARREFOUR (MP)', values: [92, 90, 89, 88, 85, 87, 86, 84, 85, 83, 81, 82] },
    { brand: 'TAUSTE (MP)', values: [88, 86, 85, 84, 82, 81, 80, 79, 78, 76, 75, 76] },
  ],
  'Sabonete Líquido': [
    { brand: 'DOVE', values: [142, 145, 141, 139, 140, 138, 137, 139, 135, 136, 138, 140] },
    { brand: 'NIVEA', values: [122, 124, 121, 120, 123, 119, 118, 117, 115, 116, 114, 115] },
    { brand: 'PROTEX', values: [115, 116, 114, 112, 113, 110, 111, 109, 108, 107, 109, 110] },
    { brand: 'PANVEL (MP)', values: [94, 92, 91, 93, 89, 88, 87, 86, 85, 84, 82, 83] },
    { brand: 'DROGARAIA (MP)', values: [90, 89, 88, 86, 85, 83, 84, 81, 82, 80, 79, 81] },
  ],
}

const columnValueLabelsPlugin: Plugin<'bar'> = {
  id: 'columnValueLabels',
  afterDatasetsDraw(chart) {
    const { ctx } = chart

    chart.data.datasets.forEach((dataset, datasetIndex) => {
      const meta = chart.getDatasetMeta(datasetIndex)
      const valueLabels = (dataset as typeof dataset & { valueLabels?: string[] }).valueLabels

      if (!valueLabels || meta.hidden) {
        return
      }

      meta.data.forEach((element, dataIndex) => {
        const barElement = element as typeof element & { x: number; y: number }
        const label = valueLabels[dataIndex]

        if (!label) {
          return
        }

        ctx.save()
        ctx.fillStyle = '#151828'
        ctx.font = '600 11px Arial, ui-sans-serif, system-ui, sans-serif'
        ctx.textAlign = 'center'
        ctx.textBaseline = 'bottom'
        ctx.fillText(label, barElement.x, barElement.y - 6)
        ctx.restore()
      })
    })
  },
}

ChartJS.register(CategoryScale, LinearScale, BarElement, Tooltip, Legend)
ChartJS.register(columnValueLabelsPlugin)

const WEEK_COLORS = [
  '#d9dee7',
  '#c4cbd8',
  '#aab3c2',
  '#8792a4',
  '#73809a',
  '#66748d',
  '#56657f',
  '#46566f',
  '#8bc1db',
  '#6eb4d1',
  '#51a3c8',
  '#c0d520',
]

const WEEK_TEXT_COLORS = [
  '#151828',
  '#151828',
  '#151828',
  '#ffffff',
  '#ffffff',
  '#ffffff',
  '#ffffff',
  '#ffffff',
  '#151828',
  '#151828',
  '#ffffff',
  '#151828',
]

function formatPriceBandLabel(value: string) {
  return value.replace('R$ ', 'R$').split(' a ')
}

function formatDensityValue(value: number, mode: MetricMode) {
  if (mode === 'share') {
    return `${value.toFixed(1)}%`
  }

  return value.toFixed(0)
}

function parsePriceBandBounds(label: string) {
  const normalized = label.replace(/\./g, '').replace(/,/g, '.')
  const matches = normalized.match(/\d+(?:\.\d+)?/g) ?? []
  const start = matches[0] ? Number(matches[0]) : 0
  const end = label.includes('+')
    ? Number.POSITIVE_INFINITY
    : matches[1]
      ? Number(matches[1])
      : start

  return { start, end }
}

function formatPriceLabelValue(value: number) {
  return value.toLocaleString('pt-BR', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })
}

function buildMergedPriceBandLabel(leftLabel: string, rightLabel: string) {
  const leftBounds = parsePriceBandBounds(leftLabel)
  const rightBounds = parsePriceBandBounds(rightLabel)

  if (!Number.isFinite(rightBounds.end)) {
    return `R$ ${formatPriceLabelValue(leftBounds.start)}+`
  }

  return `R$ ${formatPriceLabelValue(leftBounds.start)} a R$ ${formatPriceLabelValue(rightBounds.end)}`
}

function normalizeDensityBuckets(records: DensityRecord[]) {
  const ordered = records
    .slice()
    .sort((left, right) => parsePriceBandBounds(left.faixa_preco).start - parsePriceBandBounds(right.faixa_preco).start)

  if (ordered.length <= 5) {
    return ordered
  }

  const normalized = [...ordered]

  while (normalized.length > 5) {
    const right = normalized.pop()
    const left = normalized.pop()

    if (!left || !right) {
      break
    }

    normalized.push({
      formato: left.formato,
      faixa_preco: buildMergedPriceBandLabel(left.faixa_preco, right.faixa_preco),
      value: left.value + right.value,
      total: left.total ?? right.total,
    })
  }

  return normalized
}

function getLatestIndexValue(values: Array<number | null>) {
  for (let index = values.length - 1; index >= 0; index -= 1) {
    const value = values[index]

    if (typeof value === 'number' && Number.isFinite(value)) {
      return value
    }
  }

  return Number.NEGATIVE_INFINITY
}

function getRecentAverage(values: Array<number | null>, windowSize = 4) {
  const recentValues = values
    .slice(-windowSize)
    .filter((value): value is number => typeof value === 'number' && Number.isFinite(value))

  if (recentValues.length === 0) {
    return Number.NEGATIVE_INFINITY
  }

  return recentValues.reduce((sum, value) => sum + value, 0) / recentValues.length
}

export function IndiceCesta({ section }: Props) {
  const [mode, setMode] = useState<MetricMode>('share')
  const [activeSegment, setActiveSegment] = useState<SegmentName>('Fralda Infantil')

  const compactWeekLabels = section.weekly_reference_labels?.length
    ? section.weekly_reference_labels
    : WEEK_LABELS
  const weeklyBrandRows = section.weekly_brand_rows?.[activeSegment]?.length
    ? section.weekly_brand_rows[activeSegment]!
    : SEGMENT_WEEKLY_MOCK[activeSegment]
  const sortedWeeklyBrandRows = useMemo(
    () => weeklyBrandRows
      .slice()
      .sort((left, right) => {
        const latestDiff = getLatestIndexValue(right.values) - getLatestIndexValue(left.values)

        if (latestDiff !== 0) {
          return latestDiff
        }

        const recentAverageDiff = getRecentAverage(right.values) - getRecentAverage(left.values)

        if (recentAverageDiff !== 0) {
          return recentAverageDiff
        }

        return left.brand.localeCompare(right.brand, 'pt-BR')
      }),
    [weeklyBrandRows],
  )
  const basketNotes = useMemo(
    () => [
      SEGMENT_UNIT_NOTE[activeSegment] ?? 'Análise calculada com base na unidade padrão da categoria.',
      'Semanas sem captação ficam nulas para manter a estabilidade visual sem apagar a marca.',
      'As densidades verticais usam a distribuição da última semana disponível da cesta ABMAPRO.',
    ],
    [activeSegment],
  )

  const densityRecords = useMemo<DensityRecord[]>(() => (
    mode === 'share'
      ? section.brand_density_pct
        .filter(record => record.segmento === activeSegment)
        .map(record => ({
          formato: record.formato,
          faixa_preco: record.faixa_preco,
          value: record.share_pct * 100,
        }))
      : mode === 'brands'
        ? section.brand_density_count
          .filter(record => record.segmento === activeSegment)
          .map(record => ({
            formato: record.formato,
            faixa_preco: record.faixa_preco,
            value: record.marcas,
            total: record.total_marcas,
          }))
        : section.sku_density_count
          .filter(record => record.segmento === activeSegment)
          .map(record => ({
            formato: record.formato,
            faixa_preco: record.faixa_preco,
            value: record.skus,
            total: record.total_skus,
          }))
  ), [mode, activeSegment, section.brand_density_count, section.brand_density_pct, section.sku_density_count])

  const normalizedDensityRecords = useMemo(() => {
    const groupedByFormato = densityRecords.reduce<Record<string, DensityRecord[]>>((acc, record) => {
      if (!acc[record.formato]) {
        acc[record.formato] = []
      }

      acc[record.formato].push(record)
      return acc
    }, {})

    return Object.values(groupedByFormato).flatMap(records => normalizeDensityBuckets(records))
  }, [densityRecords])

  const densityMax = useMemo(() => {
    const maxValue = Math.max(...normalizedDensityRecords.map(record => record.value), 1)

    if (mode === 'share') {
      return Math.max(100, Math.ceil(maxValue / 10) * 10)
    }

    return Math.ceil(maxValue * 1.15)
  }, [mode, normalizedDensityRecords])

  const densityCards = useMemo(() => CHANNEL_ORDER.map(formato => {
    const records = normalizedDensityRecords.filter(record => record.formato === formato)

    if (records.length === 0) {
      return null
    }

    const total = records.find(record => typeof record.total === 'number')?.total
    const totalLabel = mode === 'brands'
      ? `Total: ${(total ?? 0).toLocaleString('pt-BR')} marcas`
      : mode === 'skus'
        ? `Total: ${(total ?? 0).toLocaleString('pt-BR')} SKUs`
        : 'Distribuicao percentual por faixa'

    return {
      formato,
      title: CHANNEL_LABELS[formato],
      totalLabel,
      chartData: {
        labels: records.map(record => formatPriceBandLabel(record.faixa_preco)),
        datasets: [
          {
            label: mode === 'share' ? 'Densidade %' : mode === 'brands' ? 'Marcas' : 'SKUs',
            data: records.map(record => record.value),
            valueLabels: records.map(record => formatDensityValue(record.value, mode)),
            backgroundColor: formato === 'digital_nativo' ? '#51a3c8' : '#151828',
            hoverBackgroundColor: formato === 'digital_nativo' ? '#458daa' : '#1f2437',
            borderRadius: 8,
            borderSkipped: false,
            maxBarThickness: 38,
          } as ChartData<'bar'>['datasets'][number] & { valueLabels: string[] },
        ],
      } satisfies ChartData<'bar'>,
    }
  }).filter(Boolean), [normalizedDensityRecords, mode]) as Array<{
    formato: (typeof CHANNEL_ORDER)[number]
    title: string
    totalLabel: string
    chartData: ChartData<'bar'>
  }>

  const densityOptions = useMemo<ChartOptions<'bar'>>(() => ({
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        display: false,
      },
      tooltip: {
        callbacks: {
          label: context => `${context.dataset.label}: ${formatDensityValue(Number(context.raw), mode)}`,
        },
      },
    },
    scales: {
      x: {
        grid: {
          display: false,
        },
        ticks: {
          color: '#5f667a',
          font: {
            family: 'Arial, ui-sans-serif, system-ui, sans-serif',
            size: 11,
          },
        },
        border: {
          display: false,
        },
      },
      y: {
        beginAtZero: true,
        max: densityMax,
        grid: {
          color: 'rgba(21, 24, 40, 0.06)',
        },
        ticks: {
          color: '#5f667a',
          callback: value => mode === 'share' ? `${value}%` : `${value}`,
        },
        border: {
          display: false,
        },
      },
    },
  }), [densityMax, mode])

  return (
    <section className={`card ${styles.section}`}>
      <div className={styles.sectionHeader}>
        <div>
          <span className="microlabel">Índice de preço por cesta</span>
          <h2 className={styles.sectionTitle}>{section.theme}</h2>
        </div>
        <div className={styles.keyFigures}>
          <span>{compactWeekLabels.length} semanas</span>
          <span>{activeSegment}</span>
        </div>
      </div>

      <div className={styles.segmentSelector} role="tablist" aria-label="Segmentos do índice cesta">
        {SEGMENT_OPTIONS.map(segment => (
          <button
            key={segment}
            type="button"
            role="tab"
            aria-selected={segment === activeSegment}
            className={`${styles.segmentPill} ${segment === activeSegment ? styles.segmentPillActive : ''}`}
            onClick={() => setActiveSegment(segment)}
          >
            {segment}
          </button>
        ))}
      </div>

      <div className={styles.editorialSplit}>
        <article className={styles.editorialChartCard}>
          <div className={styles.inlineHeader}>
            <span className="microlabel">Matriz semanal — {activeSegment}</span>
            <ChartExportButton
              getContainer={() =>
                document.querySelector<HTMLElement>(`[data-chart-container="indice-cesta-matriz-${activeSegment}"]`)
              }
              filename={`indice-cesta-matriz-semanal-${activeSegment}.png`}
              label="Exportar gráfico em PNG"
            />
          </div>
          <div className={styles.weeklyIndexTable} data-chart-container={`indice-cesta-matriz-${activeSegment}`}>
            <div className={`${styles.weeklyIndexRow} ${styles.weeklyIndexHeader}`}>
              <div className={styles.weeklyIndexHeaderLabel}>Marca</div>
              <div
                className={styles.weeklyIndexTrack}
                style={{ gridTemplateColumns: `repeat(${Math.max(compactWeekLabels.length, 1)}, minmax(var(--weekly-col-min, 0px), 1fr))` }}
              >
                {compactWeekLabels.map((label, index) => (
                  <div
                    key={`${activeSegment}-header-${label}`}
                    className={`${styles.weeklyIndexHeaderCell} ${index === 0 ? styles.weeklyIndexCellStart : ''} ${index === compactWeekLabels.length - 1 ? styles.weeklyIndexCellEnd : ''}`}
                  >
                    {label}
                  </div>
                ))}
              </div>
            </div>

            <div className={styles.weeklyIndexMatrix}>
              {sortedWeeklyBrandRows.map(row => (
                <div key={`${activeSegment}-${row.brand}`} className={styles.weeklyIndexRow}>
                  <div
                    className={`${styles.weeklyIndexBrand} ${row.isPrivateLabel ? styles.weeklyIndexBrandMp : ''}`}
                  >
                    {row.brand}
                  </div>
                  <div
                    className={styles.weeklyIndexTrack}
                    style={{ gridTemplateColumns: `repeat(${Math.max(compactWeekLabels.length, 1)}, minmax(var(--weekly-col-min, 0px), 1fr))` }}
                  >
                    {compactWeekLabels.map((label, weekIndex) => {
                      const value = row.values[weekIndex] ?? null

                      return (
                        <div
                          key={`${row.brand}-${label}`}
                          className={`${styles.weeklyIndexCell} ${weekIndex === 0 ? styles.weeklyIndexCellStart : ''} ${weekIndex === compactWeekLabels.length - 1 ? styles.weeklyIndexCellEnd : ''}`}
                          style={{
                            backgroundColor: WEEK_COLORS[weekIndex] ?? WEEK_COLORS[WEEK_COLORS.length - 1],
                            color: WEEK_TEXT_COLORS[weekIndex] ?? '#151828',
                          }}
                          title={`${row.brand} · ${label}: ${value ?? 'sem captação'}`}
                        >
                          <span className={styles.weeklyIndexValue}>{value ?? '—'}</span>
                        </div>
                      )
                    })}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </article>

        <aside className={styles.editorialNote}>
          <span className="microlabel">Leitura editorial</span>
          <h3>Dinâmica do Índice 100%</h3>
          <p>
            Os índices apresentados são calculados com base na média simples de preços
            capturados em todos os canais de venda monitorados.
          </p>
          <p>
            O valor <strong>100</strong> representa a média geral do mercado. Marcas com
            índice <strong>superior a 100</strong> posicionam-se com preços acima da média,
            enquanto marcas com índice <strong>inferior a 100</strong> encontram-se mais
            acessíveis frente à média consolidada da categoria no período.
          </p>
          <div className={styles.editorialNoteLegend}>
            <span className={styles.editorialNoteLegendBadge}>MARCA PRÓPRIA</span>
            <p className={styles.editorialNoteLegendText}>
              Produtos e marcas destacadas com o indicador em fundo verde representam marcas
              próprias (Private Label) identificadas e monitoradas na categoria.
            </p>
          </div>
        </aside>
      </div>

      <div className={styles.tabRow}>
        <button type="button" className={`${styles.tabChip} ${mode === 'share' ? styles.tabChipActive : ''}`} onClick={() => setMode('share')}>
          Densidade %
        </button>
        <button type="button" className={`${styles.tabChip} ${mode === 'brands' ? styles.tabChipActive : ''}`} onClick={() => setMode('brands')}>
          Marcas
        </button>
        <button type="button" className={`${styles.tabChip} ${mode === 'skus' ? styles.tabChipActive : ''}`} onClick={() => setMode('skus')}>
          SKUs
        </button>
      </div>

      <div className={styles.densityMatrix}>
        {densityCards.map(card => (
          <article key={`${mode}-${card.formato}`} className={styles.densityCard}>
            <div className={styles.densityCardHeader}>
              <div className={styles.channelHeader}>
                <h3 className={styles.channelTitle}>{card.title}</h3>
                <span className="microlabel">{card.totalLabel}</span>
              </div>
              <div className={styles.channelStats}>
                <span className={styles.densityModePill}>
                  {mode === 'share' ? 'Densidade %' : mode === 'brands' ? 'Marcas' : 'SKUs'}
                </span>
                <ChartExportButton
                  getContainer={() =>
                    document.querySelector<HTMLElement>(
                      `[data-chart-container="indice-cesta-densidade-${activeSegment}-${mode}-${card.formato}"]`,
                    )
                  }
                  filename={`indice-cesta-densidade-${activeSegment}-${mode}-${card.formato}.png`}
                  label="Exportar gráfico em PNG"
                />
              </div>
            </div>

            <div
              className={styles.densityChartWrap}
              data-chart-container={`indice-cesta-densidade-${activeSegment}-${mode}-${card.formato}`}
            >
              <Bar data={card.chartData} options={densityOptions} />
            </div>
          </article>
        ))}
        {densityCards.length === 0 && (
          <article className={styles.densityCard}>
            <div className={styles.densityCardHeader}>
              <div className={styles.channelHeader}>
                <h3 className={styles.channelTitle}>Densidade indisponível</h3>
                <span className="microlabel">Aguardando materialização do payload da cesta</span>
              </div>
            </div>
          </article>
        )}
      </div>

      <ul className={styles.notesList}>
        {basketNotes.map(note => <li key={note}>{note}</li>)}
      </ul>
    </section>
  )
}
